import React, { useState, useRef, useEffect, useCallback } from 'react';
import PQueue from 'p-queue';

const useHandleSWR = (swrData = undefined, defaultData = null, defaultBusy = false) => {
  const [ data, setData ] = useState(defaultData);
  const [ error, setError ] = useState(null);
  const [ busy, setBusy ] = useState(defaultBusy);
  const [ total, setTotal ] = useState(0);

  useEffect(() => {
    if (swrData !== undefined) { 
      if (swrData.success) {
        setError(null);
        setData(swrData.data);
        setTotal(swrData.total ? swrData.total : 0);
      }
      else {
        setError(swrData.error);
      }
    }
    setBusy(swrData === undefined);
  }, [ swrData ]);

  return { busy, data, total, error };
}

// Sometimes, we need to align or place elements depending on other dynamic elements.
// This hook returns a ref (which should be assign to a component), and the size of
// this element every time it changes.

const useComponentSize = () => {
  const ref = useRef();
  const [ size, setSize ] = useState({});

  useEffect(() => {
    if (ref && ref.current) {
      const observer = new ResizeObserver(entries => {
        let element = entries[0];
        if (element.contentRect) {
          setSize({ 
            width: element.contentRect.left + element.contentRect.right, 
            height: element.contentRect.top + element.contentRect.bottom 
          });
        }
      });
      observer.observe(ref.current);
      return () => { observer.unobserve(ref.current); };
    }
  }, []);
  return { ref, width: size.width ? size.width : 0, height: size.height ? size.height : 0 };
};

// If a click is made outside the component, then the function onOutsideClick is called.
// This function returns a ref which needs to be assigned to the component.

const useOutsideClick = (onOutsideClick) => {
  const componentRef = useRef();
  const handleOutsideClick = (e) => {
    if (!onOutsideClick || componentRef.current.contains(e.target)) {
      return;
    }
    onOutsideClick();
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => { document.removeEventListener("mousedown", handleOutsideClick); };
  });
  return componentRef;
};


// This hook allows calling many requests without having too many of them
// running concurrently.

let GlobalQueue = null;

const useNekoGlobalQueue = (params = {}) => {

  if (!GlobalQueue) {
    let { concurrency = 2 } = params;
    GlobalQueue = new PQueue({ concurrency, autoStart: true });
  }

  const runTask = async (func) => {
    return await GlobalQueue.add(func);
  }

  return { runTask };
};

// This hook handles batch of asynchronous requests, and provide everything
// the NekoProgress might need.

const isDebug = false;
const UploadQueue = new PQueue({ concurrency: 1, autoStart: false });

const useNekoTasks = () => {

  // We should be able to kill fetch requests using this
  // Interesting article: https://davidwalsh.name/cancel-fetch
  const [ controller, setController ] = useState(new AbortController());
  const refSkip = useRef(false);
  const refErrors = useRef(0);
  const [ busy, setBusy ] = useState(null);
  const [ paused, setPaused ] = useState(false);
  const [ max, setMax ] = useState(0);
  const [ error, setError ] = useState(false);
  const [ success, setSuccess ] = useState(false);
  const [ value, setValue ] = useState(0);

  const reset = async () => {
    let newController = new AbortController();
    setController(newController);
    setError(false);
    refErrors.current = 0;
    refSkip.current = false;
    setPaused(false);
    setSuccess(false);
    setValue(0);
    setMax(0);
  };

  const start = async (tasks) => {
    const runnerPromise = new Promise((resolve, reject) => {
      setError(false);
      refErrors.current = 0;
      refSkip.current = false;
      setPaused(false);
      setSuccess(false);
      setTasks(tasks);
      setBusy(true);
      if (isDebug) { console.log('Started process.'); }
      UploadQueue.start();
      UploadQueue.onIdle().then(() => {
        onIdle();
        resolve();
      }); 
    });
    return runnerPromise;
  };

  const pause = async () => {
    if (isDebug) { console.log('Paused process.'); }
    UploadQueue.pause();
    setPaused(true);
  };

  const resume = async () => {
    if (isDebug) { console.log('Resume process.'); }
    setError(false);
    setPaused(false);
    UploadQueue.start();
  };

  const stop = async () => {
    if (isDebug) { console.log('Cancelled process.'); }
    UploadQueue.pause();
    controller.abort();
    setBusy(false);
    setError(false);
    setSuccess(false);
  };

  const onIdle = async () => {
    if (isDebug) { console.log('onIdle.'); }
    setSuccess(true);
    setBusy(false);
  };

  const addTask = async (task) => {
    UploadQueue.add(async () => {
      try {
        let res = await task(controller.signal);
        if (!res || !res.success) {
          console.error('[useNekoTasks] Task returned nothing.', res);
        }
        if (!res.success) {
          refErrors.current++;
          if (!refSkip.current) {
            setError(res.error);
            pause();
          }
        }
      }
      catch (err) {
        if (err.name === 'AbortError') {
          console.log('Aborted');
          return;
        }
        else {
          refErrors.current++;
          if (!refSkip.current) {
            setError("An error was caught (more information in the console).");
            pause();
          }
        }
      }
      finally {
        setValue(value => value + 1);
      }
    });
    setMax(max => max + 1);
  }

  const setTasks = async (tasks) => {
    UploadQueue.clear();
    for (let task of tasks) {
      addTask(task);
    }
    setValue(0);
  };

  const setAlwaysSkip = useCallback((value = true) => {
    refSkip.current = true;
  }, []);

  const getErrorCount = useCallback(() => {
    return refErrors.current;
  }, []);

  return { start, stop, pause, resume, reset, 
    addTask, setAlwaysSkip, getErrorCount,
    error, success, busy, paused, value, max };
};

export { useHandleSWR, useOutsideClick, useNekoGlobalQueue, useNekoTasks, useComponentSize };